import { Injectable } from '@angular/core';
import {
  CustomerInfo,
  LOG_LEVEL,
  Purchases,
  PurchasesPackage,
  PurchasesStoreProduct,
  STORE_REPLACEMENT_MODE,
} from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { entitlementIdByPlan, subscriptionPlanByProduct } from 'src/app/config/subscription-plans';

@Injectable({
  providedIn: 'root',
})
export class RevenueCatService {
  constructor() {}

  isAvailable(): boolean {
    return Capacitor.isNativePlatform();
  }

  async setup(userId: string) {
    if (!this.isAvailable()) {
      return;
    }

    await Purchases.setLogLevel({
      level: environment.production ? LOG_LEVEL.ERROR : LOG_LEVEL.DEBUG,
    });

    const { isConfigured } = await Purchases.isConfigured();
    if (!isConfigured) {
      await Purchases.configure({
        apiKey: environment.revenuecatPublicKey,
        appUserID: userId || null,
      });
    } else if (userId) {
      const { appUserID } = await Purchases.getAppUserID();
      if (appUserID !== userId) {
        await Purchases.logIn({ appUserID: userId });
      }
    }

  }

  async getOfferings() {
    if (!this.isAvailable()) {
      return { current: null };
    }

    return Purchases.getOfferings();
  }

  async purchasePackage(
    pkg: PurchasesPackage,
    oldProductIdentifier?: string,
    replacementMode?: STORE_REPLACEMENT_MODE
  ) {
    if (!this.isAvailable()) {
      throw new Error('Assinaturas pelo navegador ainda não estão disponíveis.');
    }

    const result = await Purchases.purchasePackage({
      aPackage: pkg,
      storeProductChangeInfo: oldProductIdentifier
        ? { oldProductIdentifier, replacementMode }
        : null
    });
    return result;
  }

  async purchaseProduct(product: PurchasesStoreProduct) {
    if (!this.isAvailable()) {
      throw new Error('Assinaturas pelo navegador ainda não estão disponíveis.');
    }

    return Purchases.purchaseStoreProduct({ product });
  }

  async restorePurchases() {
    if (!this.isAvailable()) {
      throw new Error('Restauração de compra disponível apenas no aplicativo Android ou iOS.');
    }

    return Purchases.restorePurchases();
  }

  async getCustomerInfo() {
    if (!this.isAvailable()) {
      return { customerInfo: null };
    }

    return Purchases.getCustomerInfo();
  }

  async refreshCustomerInfoFromStore() {
    if (!this.isAvailable()) {
      return { customerInfo: null };
    }

    // Reenvia as compras da loja e evita decidir com informações expiradas do cache local.
    await Purchases.syncPurchases();
    await Purchases.invalidateCustomerInfoCache();
    return Purchases.getCustomerInfo();
  }

  async logIn(userId: string) {
    if (!this.isAvailable()) {
      return null;
    }

    return Purchases.logIn({ appUserID: userId });
  }

  async logOut() {
    if (!this.isAvailable()) {
      return;
    }

    await Purchases.logOut();
  }

  hasActivePremiumAccess(customerInfoResponse: CustomerInfo | { customerInfo: CustomerInfo } | null): boolean {
    const customerInfo: CustomerInfo | null = customerInfoResponse && 'customerInfo' in customerInfoResponse
      ? (customerInfoResponse as { customerInfo: CustomerInfo }).customerInfo
      : customerInfoResponse as CustomerInfo | null;
    const activeEntitlements = customerInfo?.entitlements?.active ?? {};
    const activeSubscriptions = customerInfo?.activeSubscriptions ?? [];

    const subscriptionEntitlements = [
      'premium_access',
      'pro_access',
      'obras_access',
      'construtora_access',
      'construtora_plus_access'
    ];
    if (subscriptionEntitlements.some((entitlementId) => !!activeEntitlements[entitlementId])) {
      return true;
    }

    return Array.isArray(activeSubscriptions) && activeSubscriptions.some((productId: string) =>
      this.getEntitlementIdByProduct(productId) !== null
    );
  }

  getEntitlementIdByProduct(productId: string): string | null {
    const normalizedProductId = String(productId || '').toLowerCase();
    const entitlementId = entitlementIdByPlan(subscriptionPlanByProduct(productId));
    if (entitlementId) return entitlementId;
    if (normalizedProductId.includes('ads_monthly') || normalizedProductId.includes('ads_annual')) return 'no_ads';
    return null;
  }

  hasEntitlementForProduct(customerInfoResponse: any, productId: string): boolean {
    const customerInfo = customerInfoResponse?.customerInfo ?? customerInfoResponse;
    const entitlementId = this.getEntitlementIdByProduct(productId);
    const entitlements = customerInfo?.entitlements?.all ?? {};

    if (!entitlementId) {
      return false;
    }

    return !!entitlements[entitlementId]?.isActive;
  }

  async recuperarCompra(): Promise<{ plano: string; transactionId: string }[]> {
    if (!this.isAvailable()) {
      return [];
    }

    const info = await this.getCustomerInfo();

    const compras: { plano: string; transactionId: string }[] = [];
    const customerInfo = info.customerInfo;
    const activeSubscriptions = customerInfo?.activeSubscriptions ?? [];
    const nonSubscriptions = customerInfo?.nonSubscriptionTransactions ?? [];

    if (Array.isArray(activeSubscriptions)) {
      for (const productId of activeSubscriptions) {
        compras.push({
          plano: this.getEntitlementIdByProduct(productId) || 'desconhecido',
          transactionId: productId,
        });
      }
    }

    if (Array.isArray(nonSubscriptions)) {
      for (const transaction of nonSubscriptions) {
        if (!transaction.purchaseDate) continue;

        compras.push({
          plano: this.getEntitlementIdByProduct(transaction.productIdentifier) || 'desconhecido',
          transactionId: transaction.transactionIdentifier || 'sem_id',
        });
      }
    }

    return compras;
  }
}
